import { createContext, useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Snackbar from "@mui/material/Snackbar";
import MDAlert from "components/MDAlert";

const GlobalMDAlertContext = createContext(null);
GlobalMDAlertContext.displayName = "GlobalMDAlertContext";

const defaultState = {
  open: false,
  message: "",
  color: "info",
  autoHideDuration: 3000,
  showKey: 0,
};

function GlobalMDAlertProvider({ children }) {
  const [state, setState] = useState(defaultState);

  const hideAlert = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  // Asegura que el mensaje sea siempre texto: un Error u objeto pasado por
  // error rompe el render ("Objects are not valid as a React child").
  const aTexto = (message) => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    if (message instanceof Error) return message.message;
    if (typeof message === "object") return message.message || message.Error || JSON.stringify(message);
    return String(message);
  };

  const showAlert = useCallback(({ message, color = "info", autoHideDuration } = {}) => {
    setState((s) => ({
      open: true,
      message: aTexto(message),
      color,
      autoHideDuration: autoHideDuration === undefined ? 6000 : autoHideDuration,
      showKey: s.showKey + 1,
    }));
  }, []);

  const handleSnackbarClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") {
        return;
      }
      hideAlert();
    },
    [hideAlert]
  );

  const value = useMemo(
    () => ({
      showAlert,
      hideAlert,
    }),
    [showAlert, hideAlert]
  );

  return (
    <GlobalMDAlertContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        onClose={handleSnackbarClose}
        autoHideDuration={state.autoHideDuration}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <span
          key={state.showKey}
          style={{
            display: "inline-block",
            width: "fit-content",
            maxWidth: "min(520px, calc(100vw - 32px))",
          }}
        >
          <MDAlert
            color={state.color}
            dismissible
            onDismiss={hideAlert}
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(15),
              lineHeight: 1.45,
              fontWeight: theme.typography.fontWeightMedium,
              letterSpacing: "0.01em",
              minHeight: theme.spacing(5.75),
              py: 1.125,
              px: 2,
              mb: 0,
              width: "fit-content",
              maxWidth: "inherit",
              alignSelf: "center",
              alignItems: "center",
              flexShrink: 0,
              overflow: "hidden",
              borderRadius: theme.borders.borderRadius.md,
              boxShadow:
                "0 12px 40px -12px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.12) inset",
              "& .MuiBox-root": {
                fontSize: "inherit",
                lineHeight: "inherit",
                fontWeight: "inherit",
                letterSpacing: "inherit",
                flex: "1 1 auto",
                minWidth: 0,
                width: "auto",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              },
            })}
          >
            {state.message}
          </MDAlert>
        </span>
      </Snackbar>
    </GlobalMDAlertContext.Provider>
  );
}

GlobalMDAlertProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useGlobalMDAlert() {
  const ctx = useContext(GlobalMDAlertContext);
  if (!ctx) {
    throw new Error("useGlobalMDAlert must be used inside GlobalMDAlertProvider.");
  }
  return ctx;
}

export { GlobalMDAlertProvider, useGlobalMDAlert };
